// Fetch hotel/room field overrides written via /admin/hotels and merge
// them on top of the static `hotelDetails.js` data so the website always
// reflects the CMS state without code changes.

import { useEffect, useState } from "react";
import { slugifyRoom } from "@/lib/roomRegistry";

const API = process.env.REACT_APP_BACKEND_URL || "";
const cache = new Map(); // hotel_slug → { rooms: {} }

export const useHotelCmsOverrides = (hotelSlug) => {
  const [overrides, setOverrides] = useState(() => cache.get(hotelSlug)?.rooms || {});

  useEffect(() => {
    if (!hotelSlug) return undefined;
    let cancelled = false;
    fetch(`${API}/api/hotels/${hotelSlug}/cms`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => {
        if (cancelled) return;
        const rooms = data.rooms || {};
        cache.set(hotelSlug, { rooms });
        setOverrides(rooms);
      })
      .catch(() => {/* silent — overrides are optional */});
    return () => { cancelled = true; };
  }, [hotelSlug]);

  return overrides;
};

// Merge a static room (from hotelDetails.js) with any persisted overrides.
// Inactive rooms still flow through — caller decides whether to render
// or filter them.
const mergeFields = (base, override) => {
  if (!override) return base;
  return { ...base, ...override };
};

// A room is permanently deleted if the CMS override carries either of
// these flags. `is_hidden` is kept for back-compat with rows written by
// the previous soft-hide implementation.
const isDead = (ov) => !!(ov && (ov.is_deleted || ov.is_hidden));

export const applyRoomOverrides = (baseRooms, overrides) => {
  const out = [];
  const seenSlugs = new Set();

  for (const r of baseRooms) {
    const slug = slugifyRoom(r);
    seenSlugs.add(slug);
    const ov = overrides[slug];
    // Permanently deleted base rooms are erased from the public site.
    if (isDead(ov)) continue;
    if (!ov) { out.push({ ...r, _slug: slug, _is_active: true, _is_new: false }); continue; }
    out.push({
      ...mergeFields(r, ov.fields || {}),
      image: (ov.fields && ov.fields.cover) || r.image,
      camp: r.camp || null,
      variantGroup: r.variantGroup || null,
      variantLabel: r.variantLabel || null,
      _slug: slug,
      _is_active: ov.is_active !== false,
      _is_new: !!ov.is_new,
    });
  }

  // Append rooms that exist only in CMS (is_new)
  for (const [slug, ov] of Object.entries(overrides)) {
    if (seenSlugs.has(slug)) continue;
    if (!ov.is_new) continue;
    if (isDead(ov)) continue;
    const fields = ov.fields || {};
    out.push({
      name: fields.name || { en: slug, fr: slug, es: slug },
      desc: fields.desc || {},
      surface: fields.surface || "",
      capacity: fields.capacity || {},
      amenities: fields.amenities || [],
      image: fields.cover || "",
      _slug: slug,
      _is_active: ov.is_active !== false,
      _is_new: true,
    });
  }

  return out;
};
